/**
 * useBespokeRequest — submit a Bespoke-tier interest request.
 *
 * in-app-checkout Phase 3 Session 3.6.
 *
 * Calls `POST /api/billing/bespoke-request`. The API endpoint:
 *   1. Emails the support inbox with the requesting user's profile info.
 *   2. Stamps `user_profiles.bespoke_requested_at` (idempotent — repeat
 *      requests do NOT re-email when the timestamp is already set).
 *   3. Fires the `BESPOKE_REQUESTED` lifecycle event.
 *   4. Returns `{ requested_at: string }` — the ISO timestamp.
 *
 * Design:
 *   - `retry: 0` — idempotency guarantee means the caller should see the
 *     first response, not a silently-duplicated re-send.
 *   - Takes `userId: string | undefined` (from `useAuth()` at the call site)
 *     so the hook never calls `useAuth()` internally — following the
 *     hook-arg convention for useAuth-derived state (apps/web/CLAUDE.md
 *     § 2026-05-13 footnote).
 *   - On success: invalidates `queryKeys.billing.usage(userId)` so the
 *     billing hub's redirect cascade observes the updated bespoke state
 *     immediately (for any future bespoke-awaiting badge display in the hub).
 *   - The hook is intentionally stateless across navigation — the caller
 *     (`<BespokeAwaiting>`) renders the confirmation panel using its own
 *     local `isSubmitted` state + the mutation's `isPending`/`isError`.
 *
 * Wire shape returned by `POST /api/billing/bespoke-request`:
 *   { requested_at: string }  (ISO timestamp of bespoke_requested_at)
 *
 * Error handling:
 *   - 409 ALREADY_REQUESTED: the server returns a 409 when the user already
 *     has `bespoke_requested_at` set AND the server opts to surface this
 *     explicitly (the API may also return 200 with the existing timestamp for
 *     idempotency — both are safe: the component displays the confirmation
 *     panel regardless).
 *   - 4xx / 5xx: propagated as `ApiError`; the caller renders the error state.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { BespokeResponse } from '@arcanum-hq/shared-types';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

interface BespokeRequestEnvelope {
  data: BespokeResponse;
}

export function useBespokeRequest(userId: string | undefined) {
  const queryClient = useQueryClient();

  // The server response is the locked `BespokeResponse` (`{ submitted: true }`).
  // The body is empty — `tier_id` is optional server-side (the in-app request is
  // implicitly for the Bespoke tier; non-load-bearing). Consumers branch on
  // onSuccess firing, not on the returned value.
  return useMutation<BespokeResponse, Error, void>({
    mutationFn: async () => {
      const envelope = await api.post<BespokeRequestEnvelope>(
        '/api/billing/bespoke-request',
        {}
      );
      return envelope.data;
    },
    retry: 0,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.billing.usage(userId),
      });
      // Invalidate the user profile cache so the persistent bespoke-awaiting
      // banner in BillingHub reads the updated bespoke_requested_at immediately
      // without requiring a reload (FIX 1 — in-app-checkout Phase 3 /review gap).
      void queryClient.invalidateQueries({
        queryKey: queryKeys.user.profile(),
      });
    },
  });
}
